/* Product page (sections/main-product.liquid): keeps the sticky details column reachable.
   A sticky column taller than the window would pin its top and hide its end — the buy buttons'
   neighbours, the accordion — until the whole gallery had scrolled past. So the column's top
   offset is the smaller of «just under the header» and «its bottom 24 px above the window's»:
   a short column pins at the top, a tall one scrolls with the page until its end is in view and
   pins there. Recomputed whenever the column or the window changes size (a variant change, an
   accordion row opening). */

const GAP = 24;

function fit(info) {
  const root = getComputedStyle(document.documentElement);
  const header = parseFloat(root.getPropertyValue('--header-offset')) || 0;
  const top = header + 36;
  const needed = window.innerHeight - info.offsetHeight - GAP;
  info.style.setProperty('--pdp-info-top', `${Math.min(top, needed)}px`);
}

function wire(info) {
  if (info.dataset.fitted) return;
  info.dataset.fitted = '1';
  const run = () => fit(info);
  new ResizeObserver(run).observe(info);
  window.addEventListener('resize', run, { passive: true });
  run();
}

/* ---------- Lightbox ----------
   The gallery's zoom buttons open the lightbox dialog on their own image. The overlay system
   (assets/overlay.js) opens and closes it; this steps through the images — arrows, ← →, swipe
   (the track scroll-snaps) — and keeps the thumbnails in step. */

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

class MwLightbox extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.track = this.querySelector('[data-lightbox-track]');
    this.slides = [...this.querySelectorAll('[data-lightbox-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-lightbox-go]')];
    this.index = 0;
    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-lightbox-step]');
      if (step) return this.go(this.index + Number(step.dataset.lightboxStep));
      const thumb = event.target.closest('[data-lightbox-go]');
      if (thumb) return this.go(Number(thumb.dataset.lightboxGo));
    });
    this.closest('dialog')?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
    });
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.mark(Number(entry.target.dataset.lightboxSlide));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  go(index, instant = false) {
    if (!this.slides.length) return;
    const next = (index + this.slides.length) % this.slides.length;
    this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: instant || reduced.matches ? 'auto' : 'smooth' });
    this.mark(next);
  }

  mark(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

if (!customElements.get('mw-lightbox')) customElements.define('mw-lightbox', MwLightbox);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-lightbox-open]');
  if (!opener) return;
  const dialog = document.getElementById(opener.getAttribute('aria-controls'));
  const box = dialog?.querySelector('mw-lightbox');
  if (!dialog || !box || !window.Munkwell?.overlay) return;
  window.Munkwell.overlay.open(dialog, opener);
  // Open on the image that was clicked, without sliding there from the first one.
  requestAnimationFrame(() => box.go(Number(opener.dataset.lightboxOpen), true));
});

const scan = () => document.querySelectorAll('.pdp--sticky .pdp__info, .pdp--sticky.pdp--gallery-thumbs .pdp__media').forEach(wire);
scan();
// The variant picker re-renders the column; pick the new one up.
new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });

/* «Buy 2 — save 15 %»: sets the quantity of the form beside it; the cart applies the discount. */
document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-volume-qty]');
  if (!pill) return;
  const input = pill.closest('.pdp-buy')?.querySelector('input[name="quantity"]');
  if (!input) return;
  input.value = pill.dataset.volumeQty;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  pill.setAttribute('aria-pressed', 'true');
});
